import { type Client } from 'graphql-ws'

export class WebSocketClient {
  private wsClient: Client | undefined

  constructor(wsClient: Client | undefined) {
    this.wsClient = wsClient
  }

  subscribe<T = unknown>({
    query,
    variables,
    onNext,
    onError,
    onComplete,
  }: {
    query: string
    variables?: Record<string, unknown>
    onNext: (data: T) => void
    onError?: (error: Error) => void
    onComplete?: () => void
  }): () => void {
    if (!this.wsClient) {
      throw new Error('Client is not intialized')
    }

    const unsubscribe = this.wsClient.subscribe(
      {
        query,
        variables,
      },
      {
        next: result => {
          if (result.data) {
            onNext(result.data as T)
          }
          if (result.errors) {
            onError?.(new Error(result.errors.map(e => e.message).join(', ')))
          }
        },
        error: error => {
          if (error instanceof Error) {
            onError?.(error)
          } else if (typeof error === 'string') {
            onError?.(new Error(error))
          } else {
            onError?.(new Error('Unknown websocket error occurred'))
          }
        },
        complete: () => {
          onComplete?.()
        },
      },
    )

    return unsubscribe
  }

  async *iterate<T = unknown>({
    query,
    variables,
  }: {
    query: string
    variables?: Record<string, unknown>
  }): AsyncIterableIterator<T> {
    if (!this.wsClient) {
      throw Error('Client is not initialized')
    }

    const iterator = this.wsClient.iterate<{
      data?: T
      errors?: Array<{ message: string }>
    }>({
      query,
      variables,
    })

    // eslint-disable-next-line no-restricted-syntax
    for await (const result of iterator) {
      if (result.data) {
        yield result.data as T
      }
      if (result.errors) {
        throw new Error(result.errors.map(e => e.message).join(', '))
      }
    }
  }

  // Method to cleanup/close the websocket connection
  disconnect(): void {
    if (!this.wsClient) {
      return
    }

    this.wsClient.dispose()
  }
}
